/**
 * Composable for managing page scroll state.
 * Prevents/enables scrolling on the body element, useful for modals and overlays.
 */
export const usePageScroll = () => {
    const isScrollLocked = ref(false);
    const originalOverflow = ref<string>('');
    const originalPaddingRight = ref<string>('');

    /**
     * Get the current scrollbar width to prevent layout shift
     */
    const getScrollbarWidth = (): number => {
        if (!import.meta.client) return 0;

        const outer = document.createElement('div');
        outer.style.visibility = 'hidden';
        outer.style.overflow = 'scroll';
        document.body.appendChild(outer);

        const inner = document.createElement('div');
        outer.appendChild(inner);

        const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

        outer.parentNode?.removeChild(outer);

        return scrollbarWidth;
    };

    /**
     * Lock page scrolling
     */
    const lockScroll = () => {
        if (!import.meta.client || isScrollLocked.value) return;

        const scrollbarWidth = getScrollbarWidth();

        // Save original values
        originalOverflow.value = document.body.style.overflow;
        originalPaddingRight.value = document.body.style.paddingRight;

        // Prevent layout shift by adding padding equal to scrollbar width
        if (scrollbarWidth > 0) {
            // document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        // Lock scroll
        document.body.style.overflow = 'hidden';
        isScrollLocked.value = true;
    };

    /**
     * Unlock page scrolling
     */
    const unlockScroll = () => {
        if (!import.meta.client || !isScrollLocked.value) return;

        // Restore original values
        document.body.style.overflow = originalOverflow.value;
        document.body.style.paddingRight = originalPaddingRight.value;
        isScrollLocked.value = false;
    };

    /**
     * Toggle scroll lock state
     */
    const toggleScroll = () => {
        if (isScrollLocked.value) {
            unlockScroll();
        } else {
            lockScroll();
        }
    };

    // Clean up on unmount
    onUnmounted(() => {
        if (isScrollLocked.value) {
            unlockScroll();
        }
    });

    return {
        /** Whether scroll is currently locked */
        isScrollLocked: readonly(isScrollLocked),
        /** Lock page scrolling */
        lockScroll,
        /** Unlock page scrolling */
        unlockScroll,
        /** Toggle scroll lock state */
        toggleScroll,
    };
};